import { useCallback, useEffect, useState } from 'react';
import { closingApi, getStoredUser, type PeriodStatus, type ClosingRecord, type YearStatus } from '../api/client.js';
import { formatAmount } from '../components/Layout.js';
import { ErrorBanner } from '../components/States.js';
import { roleCan, permHint } from '../utils/permissions.js';

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function Closing() {
  const canManage = roleCan('closing:manage');
  const [period, setPeriod] = useState(currentPeriod());
  const [status, setStatus] = useState<PeriodStatus | null>(null);
  const [closings, setClosings] = useState<ClosingRecord[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [yearStatus, setYearStatus] = useState<YearStatus | null>(null);
  const [yearBusy, setYearBusy] = useState(false);

  const load = useCallback(async (p: string) => {
    setError('');
    try {
      const [st, list, yst] = await Promise.all([
        closingApi.status(p),
        closingApi.periods(),
        closingApi.yearStatus(p.slice(0, 4)),
      ]);
      setStatus(st);
      setClosings(list.closings);
      setYearStatus(yst);
    } catch (e: any) {
      setError(e.message || '加载失败');
    }
  }, []);

  useEffect(() => {
    load(period);
  }, [period, load]);

  const actor = getStoredUser()?.displayName || 'admin';

  const doClose = async () => {
    if (!status) return;
    if (!window.confirm(`确认结账 ${period}？将自动生成结转损益凭证并锁定该期间。`)) return;
    setBusy(true);
    setMessage('');
    try {
      const res = await closingApi.close(period, actor);
      setMessage(
        res.closingReference
          ? `✅ ${period} 已结账，生成结转凭证 ${res.closingReference}（结转 ${res.transferredAccounts} 个损益科目，本年利润 ${formatAmount(res.netProfit)}）`
          : `✅ ${period} 已结账（当期无损益，无需结转凭证）`
      );
      await load(period);
    } catch (e: any) {
      setError(e.message || '结账失败');
    } finally {
      setBusy(false);
    }
  };

  const doReopen = async () => {
    if (!window.confirm(`确认反结账 ${period}？将解锁期间并作废结转凭证。`)) return;
    setBusy(true);
    setMessage('');
    try {
      const res = await closingApi.reopen(period);
      setMessage(`🔓 ${period} 已反结账${res.voidedReference ? `，作废结转凭证 ${res.voidedReference}` : ''}`);
      await load(period);
    } catch (e: any) {
      setError(e.message || '反结账失败');
    } finally {
      setBusy(false);
    }
  };

  const year = period.slice(0, 4);

  const doYearClose = async () => {
    if (!yearStatus) return;
    if (!window.confirm(`确认年终结转 ${year}？将把本年利润 ${formatAmount(yearStatus.netProfit)} 结转至利润分配。`)) return;
    setYearBusy(true);
    setMessage('');
    try {
      const res = await closingApi.yearClose(year, actor);
      setMessage(
        res.annualReference
          ? `✅ ${year} 年终结转完成，生成凭证 ${res.annualReference}（本年利润→利润分配 ${formatAmount(res.netProfit)}）`
          : `✅ ${year} 年终结转完成（本年利润为零，无需结转）`
      );
      await load(period);
    } catch (e: any) {
      setError(e.message || '年终结转失败');
    } finally {
      setYearBusy(false);
    }
  };

  const doYearReopen = async () => {
    if (!window.confirm(`确认反年结 ${year}？将删除年结凭证并解锁该年度结转。`)) return;
    setYearBusy(true);
    setMessage('');
    try {
      const res = await closingApi.yearReopen(year);
      setMessage(`🔓 ${year} 已反年结${res.voidedReference ? `，作废年结凭证 ${res.voidedReference}` : ''}`);
      await load(period);
    } catch (e: any) {
      setError(e.message || '反年结失败');
    } finally {
      setYearBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-slate-800">期末结账</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">结账期间</label>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load(period)} />}
      {message && <p className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">{message}</p>}

      {status && (
        <div className="grid md:grid-cols-3 gap-4">
          {/* 结账概览卡 */}
          <div className="bg-white rounded-xl shadow p-5 md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-700">{status.period} 经营结果</h3>
              <StatusBadge status={status} />
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-400 mb-1">收入合计</p>
                <p className="text-lg font-bold font-mono text-green-600">{formatAmount(status.totalRevenue)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">费用合计</p>
                <p className="text-lg font-bold font-mono text-orange-600">{formatAmount(status.totalExpense)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">本年利润</p>
                <p className={`text-lg font-bold font-mono ${status.netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatAmount(status.netProfit)}
                </p>
              </div>
            </div>

            {/* 结转前置检查 */}
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-1 text-sm">
              <PreCheck label="未过账草稿" count={status.draftCount} />
              <PreCheck label="待审核凭证" count={status.postedCount} />
              {status.blockReason && !status.closed && (
                <p className="text-amber-600 text-xs mt-2">⚠ {status.blockReason}</p>
              )}
              {status.closed && status.closingReference && (
                <p className="text-slate-500 text-xs mt-2">
                  结转凭证：<span className="font-mono">{status.closingReference}</span>
                  {status.closedBy ? ` · 结账人 ${status.closedBy}` : ''}
                  {status.closedAt ? ` · ${String(status.closedAt).slice(0, 10)}` : ''}
                </p>
              )}
            </div>

            <div className="mt-5 flex gap-3">
              {!status.closed ? (
                <button
                  onClick={doClose}
                  disabled={!status.canClose || busy || !canManage}
                  title={permHint('closing:manage')}
                  className="btn btn-primary"
                >
                  {busy ? '处理中…' : '🔒 结账（结转损益并锁定）'}
                </button>
              ) : (
                <button
                  onClick={doReopen}
                  disabled={busy || !canManage}
                  title={permHint('closing:manage')}
                  className="btn btn-warn"
                >
                  {busy ? '处理中…' : '🔓 反结账（解锁期间）'}
                </button>
              )}
            </div>
          </div>

          {/* 结账历史 */}
          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="font-medium text-slate-700 mb-3">结账记录</h3>
            {closings.length === 0 && (
              <div className="py-8 text-center animate-fade-in-up">
                <div className="w-12 h-12 rounded-2xl bg-brand-50 grid place-items-center text-2xl mx-auto">🔒</div>
                <p className="text-sm font-medium text-slate-600 mt-1">暂无结账记录</p>
                <p className="text-xs text-slate-400">结账后该期间凭证将被锁定，不可新增或修改</p>
              </div>
            )}
            <ul className="space-y-2">
              {closings.map((c) => (
                <li key={c.period} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-slate-600">{c.period}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    c.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {c.status === 'closed' ? '已锁定' : '已反结'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
       </div>
      )}

      {yearStatus && (
        <div className="card card-pad">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="section-title">🗓️ {year} 年终结转</h3>
              <p className="page-subtitle mt-0.5">将“本年利润 3131”累计余额结转至“利润分配 3141”</p>
            </div>
            <YearBadge ys={yearStatus} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-slate-400 mb-1">本年利润余额</p>
              <p className={`kpi-value ${yearStatus.netProfit >= 0 ? 'text-brand-600' : 'text-red-600'}`}>
                {formatAmount(yearStatus.netProfit)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">未结账月份</p>
              <p className={`kpi-value ${yearStatus.openMonths.length === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                {yearStatus.openMonths.length === 0 ? '全部已结' : `${yearStatus.openMonths.length} 个`}
              </p>
            </div>
            <div className="hidden sm:block">
              <p className="text-xs text-slate-400 mb-1">年结凭证</p>
              <p className="text-sm font-mono text-slate-600 mt-1.5">{yearStatus.annualReference || '—'}</p>
            </div>
          </div>

          {yearStatus.openMonths.length > 0 && (
            <p className="text-amber-600 text-xs mt-3">⚠ 需先完成以下月份的结账：{yearStatus.openMonths.join('、')}</p>
          )}

          <div className="mt-5 flex gap-3">
            {!yearStatus.alreadyTransferred ? (
              <button onClick={doYearClose} disabled={!yearStatus.canClose || yearBusy || !canManage} title={permHint('closing:manage')} className="btn btn-primary">
                {yearBusy ? '处理中…' : '🧾 年终结转（本年利润→利润分配）'}
              </button>
            ) : (
              <button onClick={doYearReopen} disabled={yearBusy || !canManage} title={permHint('closing:manage')} className="btn btn-warn">
                {yearBusy ? '处理中…' : '🔓 反年结'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function YearBadge({ ys }: { ys: YearStatus }) {
  if (ys.alreadyTransferred) {
    return <span className="badge badge-green">✓ 已完成年结</span>;
  }
  if (ys.canClose) {
    return <span className="badge badge-brand">可年结</span>;
  }
  return <span className="badge badge-amber">未就绪</span>;
}

function StatusBadge({ status }: { status: PeriodStatus }) {
  if (status.closed) {
    return <span className="px-2.5 py-1 rounded-full text-xs bg-green-100 text-green-700">🔒 已结账锁定</span>;
  }
  if (status.canClose) {
    return <span className="px-2.5 py-1 rounded-full text-xs bg-blue-100 text-blue-700">可结账</span>;
  }
  return <span className="px-2.5 py-1 rounded-full text-xs bg-amber-100 text-amber-700">有未完成凭证</span>;
}

function PreCheck({ label, count }: { label: string; count: number }) {
  const ok = count === 0;
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={ok ? 'text-green-600' : 'text-red-600 font-medium'}>
        {ok ? '✔ 无' : `${count} 张`}
      </span>
    </div>
  );
}
